import { useEffect, useState } from "react";
import {
    LinkDismissRegular
} from "@fluentui/react-icons";
import { Label, makeStyles, shorthands } from "@fluentui/react-components";
import { IRemoveSharingLinksActionRequest, IRemoveSharingLinksSelection } from "@interfaces/actions/sharing-link-actions.interfaces";
import { IRemoveSharingLinksActionFormProps } from "./remove-sharing-links-action-form.props";
import WorkspaceCard from "components/common/workspace-card/workspace-card";
import SharingLinkCard from "components/common/sharing-link-card/sharing-link-card";
import ItemCard from "components/common/item-card/item-card";
import { getSharingLinkPermissionDisplayText } from "lib/sharing-link-permissions";
import ActionFormButtons from "components/actions-common/action-buttons/action-form-buttons";
import ActionFormRequestMessage from "components/actions-common/action-form-request-message/action-form-request-message";
import { ILocaleStrings } from "loc/locale.interfaces";
import { useL10n } from "context/l10n-context";

const layoutStyles = makeStyles({
    mainWrapper: {
        display: 'flex',
        flexDirection: 'column'
    },
    itemWrapper: {
        ...shorthands.margin('1em', 0, 0, 0)
    }
});

export default function RemoveSharingLinksActionForm(props: IRemoveSharingLinksActionFormProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();

    const [selection, setSelection] = useState<IRemoveSharingLinksSelection>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [canRemoveSharingLink, setCanRemoveSharingLink] = useState<boolean>(true);
    const [canRemoveItem, setCanRemoveItem] = useState<boolean>(true);
    const [requestMessage, setRequestMessage] = useState<string>(``);

    const onSubmitHandler = async (): Promise<any> => {
        if (!props.onSubmit) {
            return Promise.reject();
        }

        const requestModel: IRemoveSharingLinksActionRequest = {};

        // Always include workspace
        if (selection?.workspace)
            requestModel.workspace = { workspaceUrl: selection.workspace.url };

        // Include sharing item if present
        if (selection?.sharingItem)
            requestModel.sharingItem = { 
                itemUniqueId: selection.sharingItem.itemUniqueId,
                listId: selection.sharingItem.listId
            };

        // Include sharing link if present (for specific link removal)
        if (selection?.sharingLink)
            requestModel.sharingLink = { 
                shareId: selection.sharingLink.shareId,
                linkUrl: selection.sharingLink.linkUrl
            };

        setIsSubmitting(true);

        return props.onSubmit("/api/actions/remove-sharing-links", requestModel).then(
            (data: any) => {
                console.log(`RemoveSharingLinksActionForm -> onSubmitHandler -> success ->`, data);
                setIsSubmitting(false);
                return data;
            },
            (error: any) => {
                console.log(`RemoveSharingLinksActionForm -> onSubmitHandler -> error ->`, error);
                setIsSubmitting(false);
                return Promise.reject(error);
            });
    }

    useEffect(() => {
        const selection: IRemoveSharingLinksSelection = {};
        if (props.model) {
            if (props.model.workspace) {
                selection.workspace = {
                    url: props.model.workspace.url,
                    name: props.model.workspace.name,
                    workspaceType: props.model.workspace.workspaceType
                };
            }
            if (props.model.sharingItem) {
                selection.sharingItem = {
                    itemUniqueId: props.model.sharingItem.itemUniqueId,
                    itemName: props.model.sharingItem.itemName,
                    itemUrl: props.model.sharingItem.itemUrl,
                    itemType: props.model.sharingItem.itemType,
                    listId: props.model.sharingItem.listId
                };
            }
            if (props.model.sharingLink) {
                selection.sharingLink = {
                    shareId: props.model.sharingLink.shareId,
                    linkUrl: props.model.sharingLink.linkUrl,
                    itemName: props.model.sharingLink.itemName,
                    itemUrl: props.model.sharingLink.itemUrl,
                    itemUniqueId: props.model.sharingLink.itemUniqueId,
                    permissionType: props.model.sharingLink.permissionType
                };
            }
        }
        setSelection(selection);
    }, [props.model]);

    useEffect(() => {
        if (isSubmitting) {
            setCanRemoveSharingLink(false);
            setCanRemoveItem(false);
        }
        else {
            setCanRemoveSharingLink(true);
            // Can only remove item if there's no sharing link (sharing link must be dismissed first)
            setCanRemoveItem(!selection?.sharingLink);

            // Set the request message based on the selection (3 scenarios)
            var requestMessageKey: keyof ILocaleStrings = "unknown";

            const params = {
                permissionType: getSharingLinkPermissionDisplayText(selection?.sharingLink?.permissionType, t),
                shareId: selection?.sharingLink?.shareId,
                itemName: selection?.sharingItem?.itemName || selection?.sharingLink?.itemName,
                workspaceTitle: selection?.workspace?.name
            };

            if (selection?.workspace && selection?.sharingItem && selection?.sharingLink) {
                // Scenario 1: Remove specific sharing link from item
                requestMessageKey = "ppaRemoveSharingLinksActionRequestMessageSpecificLink";
            }
            else if (selection?.workspace && selection?.sharingItem) {
                // Scenario 2: Remove all sharing links from item
                requestMessageKey = "ppaRemoveSharingLinksActionRequestMessageAllLinksFromItem";
            }
            else if (selection?.workspace) {
                // Scenario 3: Remove all sharing links from workspace
                requestMessageKey = "ppaRemoveSharingLinksActionRequestMessageAllLinksFromWorkspace";
            }

            setRequestMessage(t(requestMessageKey, params));
        }
    }, [isSubmitting, selection, t]);

    const removeSharingLink = (): void => {
        setSelection({
            ...selection,
            sharingLink: undefined
        });
    }

    const removeItem = (): void => {
        setSelection({
            ...selection,
            sharingItem: undefined
        });
    }


    return (
        <div className={styles.mainWrapper}>
            {selection?.sharingLink &&
                <div className={styles.itemWrapper}>
                    <Label>{t('ppaSharingLink')}</Label>
                    <SharingLinkCard
                        item={{
                            shareId: selection.sharingLink.shareId,
                            linkUrl: selection.sharingLink.linkUrl,
                            itemName: selection.sharingLink.itemName,
                            itemUrl: selection.sharingLink.itemUrl,
                            itemUniqueId: selection.sharingLink.itemUniqueId,
                            permissionType: selection.sharingLink.permissionType
                        }}
                        onDismissClick={canRemoveSharingLink ? removeSharingLink : undefined} />
                </div>}
            {selection?.sharingItem &&
                <div className={styles.itemWrapper}>
                    <Label>{t('ppaItem')}</Label>
                    <ItemCard
                        item={{
                            itemUniqueId: selection.sharingItem.itemUniqueId,
                            itemName: selection.sharingItem.itemName,
                            itemUrl: selection.sharingItem.itemUrl,
                            itemType: selection.sharingItem.itemType,
                            listId: selection.sharingItem.listId
                        }}
                        onDismissClick={canRemoveItem ? removeItem : undefined} />
                </div>}
            {selection?.workspace &&
                <div className={styles.itemWrapper}>
                    <Label>{t('ppaWorkspace')}</Label>
                    <WorkspaceCard
                        item={{ 
                            title: selection.workspace.name, 
                            siteUrl: selection.workspace.url,
                            workspaceType: selection.workspace.workspaceType 
                        }}
                        onDismissClick={undefined} />
                </div>}
            <ActionFormRequestMessage
                icon={<LinkDismissRegular />}
                message={requestMessage} />
            <ActionFormButtons
                onSubmit={onSubmitHandler}
                onCancel={props.onCancel} />
        </div>
    );
}